import type { Ref } from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import dayjs from '../../utils/dayjs';
import { CalendarDateBasis, CalendarEvent } from './types';
import CalendarSelectedDayEntryCard from './CalendarSelectedDayEntryCard';
import { getCalendarTypography, getCalendarUiPalette } from './calendarPalette';

interface CalendarSelectedDayPanelProps {
  dateBasis: CalendarDateBasis;
  selectedDate: Date;
  events: CalendarEvent[];
  onSelectEvent: (event: CalendarEvent) => void;
  panelRef?: Ref<HTMLDivElement>;
}

export default function CalendarSelectedDayPanel({
  dateBasis,
  selectedDate,
  events,
  onSelectEvent,
  panelRef,
}: Readonly<CalendarSelectedDayPanelProps>) {
  const theme = useTheme();
  const calendarUiPalette = getCalendarUiPalette(theme);
  const calendarTypography = getCalendarTypography(theme);
  const isToday = dayjs(selectedDate).isSame(dayjs(), 'day');
  const dateBasisLabel = dateBasis === 'dueDate' ? 'Viewing by Due Date' : 'Viewing by Entry Date';

  return (
    <Box
      ref={panelRef}
      data-cy="calendar-selected-day-panel"
      sx={{
        mt: 3.5,
        py: 2.5,
        px: 2.5,
        borderTop: `1px solid ${calendarUiPalette.selectedDayPanel.divider}`,
        backgroundColor: calendarUiPalette.selectedDayPanel.washBackground,
        borderRadius: '18px',
      }}
    >
      <Typography
        sx={{
          ...calendarTypography.eyebrowCompact,
          color: calendarUiPalette.selectedDayPanel.eyebrowText,
          fontSize: '0.7rem',
          fontWeight: 600,
          letterSpacing: '0.12em',
          mb: 1,
          textAlign: 'left',
        }}
      >
        Selected Day
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          flexDirection: 'row',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Typography
            sx={{
              ...theme.typography.h5,
              color: calendarUiPalette.selectedDayPanel.title,
              fontWeight: theme.typography.fontWeightBold,
            }}
          >
            {dayjs(selectedDate).format('MMMM D')}
          </Typography>
          {isToday ? (
            <Chip
              label="Today"
              size="small"
              sx={{
                height: 24,
                backgroundColor: calendarUiPalette.selectedDayPanel.todayChipBackground,
                color: calendarUiPalette.selectedDayPanel.todayChipText,
                ...calendarTypography.chip,
                textTransform: 'uppercase',
              }}
            />
          ) : null}
        </Box>
        <Typography
          sx={{
            ...theme.typography.body2,
            color: calendarUiPalette.selectedDayPanel.summaryText,
            fontWeight: theme.typography.fontWeightMedium,
            ml: 'auto',
          }}
        >
          {events.length} Active {events.length === 1 ? 'Entry' : 'Entries'} • {dateBasisLabel}
        </Typography>
      </Box>

      {events.length > 0 ? (
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 0.72fr))',
            justifyContent: 'space-between',
            columnGap: 4.5,
            rowGap: 2,
            mx: 3.5,
          }}
        >
          {events.map((event) => (
            <CalendarSelectedDayEntryCard key={`${event.logtype}-${event.id}`} event={event} onClick={onSelectEvent} />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            mt: 1,
            px: 2.5,
            py: 3,
            borderRadius: '18px',
            backgroundColor: calendarUiPalette.selectedDayPanel.emptyBackground,
            border: `1px dashed ${calendarUiPalette.selectedDayPanel.emptyBorder}`,
          }}
        >
          <Typography
            sx={{
              ...theme.typography.body1,
              color: calendarUiPalette.selectedDayPanel.emptyText,
              fontWeight: theme.typography.fontWeightMedium,
            }}
          >
            Select a date to see items.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
